import type { ReploSiteEntitlementRejectedModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Button } from "@replohq/lattice/components/button/Button";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { showSubscriptionUpdateSuccessToast } from "@v2/features/billing/billingToasts";
import useApplySubscriptionChangeMutation from "@v2/features/billing/hooks/useApplySubscriptionChangeMutation";
import { getProratedCharge } from "@v2/features/billing/planPriceUtils";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "@v2/features/billing/shared/sharedBillingUtils";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import { useModal } from "@v2/store/modalStore";
import { billingRouter } from "@v2/trpc/routes/billing";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { getProjectLabels } from "@v2/utils/project-labels";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { DEPRECATED_formatPriceInUsdDollars } from "replo-utils/lib/format";
import { ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH, AddOn } from "schemas/addOns";

const ReploSiteEntitlementRejectedModal: React.FC<
  ReploSiteEntitlementRejectedModalProps
> = ({ explanationMessage }) => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription } = useSubscriptionDetails();

  const { data: reploSitesUsage } = workspaceRouter.getReploSitesUsage.useQuery(
    workspace?.id ?? skipToken,
  );

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspace?.id && activeSubscription?.id
        ? {
            workspaceId: workspace.id,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useApplySubscriptionChangeMutation();

  const closeModal = () => {
    modal.closeModal({ type: "reploSiteEntitlementRejectedModal" });
  };

  if (
    !workspace ||
    !reploSitesUsage ||
    reploSitesUsage.maximum === null // If they have unlimited sites, we don't need to show this modal
  ) {
    return null;
  }

  const labels = getProjectLabels("replo");

  let headerText: string;
  let content: React.ReactNode;

  if (!activeSubscription) {
    headerText = "Upgrade to continue";
    content = (
      <FreeUserUpgradeRequiredContent
        explanationMessage={explanationMessage}
        onClose={closeModal}
        workspaceId={workspace.id}
      />
    );
  } else {
    if (!nextBillingDate) {
      return null;
    }

    const isFromEntitlement = reploSitesUsage.isFromEntitlement;

    const activeSubscriptionIsYearly =
      activeSubscription.billingInterval === "yearly";

    const additionalSiteFullPeriodPrice = activeSubscriptionIsYearly
      ? ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH * 12
      : ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH;

    const formattedAdditionalSitePrice = activeSubscriptionIsYearly
      ? `${DEPRECATED_formatPriceInUsdDollars({ amount: additionalSiteFullPeriodPrice, currency: "usd" })}/year`
      : `${DEPRECATED_formatPriceInUsdDollars({ amount: additionalSiteFullPeriodPrice, currency: "usd" })}/month`;

    const formattedNextBillingDate = new Date(
      nextBillingDate,
    ).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    const proratedCharge = getProratedCharge({
      billingInterval: activeSubscription.billingInterval,
      nextBillingDate: new Date(nextBillingDate),
      fullPeriodPrice: additionalSiteFullPeriodPrice,
    });

    const handleConfirm = async () => {
      const currentAdditionalSites =
        getCountOfAdditionalSitesInSubscription(activeSubscription);

      const currentAdditionalProjects =
        getCountOfAdditionalProjectsInSubscription(activeSubscription);

      const newAdditionalSites = currentAdditionalSites + 1;

      const currentAdditionalPublishedElementBundles =
        getCountOfAdditionalPublishedElementBundlesInSubscription(
          activeSubscription,
        );

      analytics.logEvent(
        "billing.reploSiteEntitlementRejectedModal.purchasedAddOn",
        {
          currentPlan: activeSubscription.billingTier,
          newAdditionalReploSites: newAdditionalSites,
        },
      );

      const addOns = AddOn.buildFromCounts({
        additionalSites: newAdditionalSites,
        additionalProjects: currentAdditionalProjects,
        additionalPublishedElementBundles:
          currentAdditionalPublishedElementBundles,
      });

      await applySubscriptionChange({
        workspaceId: workspace.id,
        billingTier: activeSubscription.billingTier,
        billingInterval: activeSubscription.billingInterval,
        addOns,
        source: "direct.billingDashboard",
      });
      showSubscriptionUpdateSuccessToast();
      closeModal();
    };

    if (activeSubscription.isManagedByReplo || isFromEntitlement) {
      headerText = `Add active ${labels.plural}`;
    } else {
      headerText = `Add an active ${labels.singular}`;
    }

    if (activeSubscription.isManagedByReplo) {
      content = (
        <PlanManagedByReploContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
          labels={labels}
        />
      );
    } else if (isFromEntitlement) {
      content = (
        <IsFromEntitlementContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
          labels={labels}
        />
      );
    } else {
      content = (
        <AddShopContent
          explanationMessage={explanationMessage}
          formattedAdditionalSitePrice={formattedAdditionalSitePrice}
          formattedNextBillingDate={formattedNextBillingDate}
          proratedCharge={proratedCharge}
          newTotalSites={reploSitesUsage.maximum + 1}
          isLoading={isLoadingSubscriptionUpdate}
          onConfirm={() => void handleConfirm()}
          onClose={closeModal}
          labels={labels}
        />
      );
    }
  }

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      title={headerText}
      media={
        <img
          src="/images/billing/shop-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      {content}
    </SplitModal>
  );
};

type Labels = ReturnType<typeof getProjectLabels>;

const AddShopContent = ({
  explanationMessage,
  formattedAdditionalSitePrice,
  formattedNextBillingDate,
  proratedCharge,
  newTotalSites,
  isLoading,
  onConfirm,
  onClose,
  labels,
}: {
  explanationMessage: string;
  formattedAdditionalSitePrice: string;
  formattedNextBillingDate: string;
  proratedCharge: number;
  newTotalSites: number;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between h-full">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Adding 1 {labels.singular} will increase your workspace bill by{" "}
          <span className="font-bold">{formattedAdditionalSitePrice}</span>{" "}
          starting {formattedNextBillingDate}, bringing your total to{" "}
          <span className="font-bold">
            {newTotalSites} {labels.plural}.
          </span>
          <br />
          <br /> A prorated charge of{" "}
          <span className="font-bold">
            {DEPRECATED_formatPriceInUsdDollars({
              amount: proratedCharge,
              currency: "usd",
            })}
          </span>{" "}
          will be applied today to your card on file.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <Button
          variant="primary"
          size="base"
          onClick={onConfirm}
          loading={isLoading}
        >
          Confirm
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const FreeUserUpgradeRequiredContent = ({
  explanationMessage,
  onClose,
  workspaceId,
}: {
  explanationMessage: string;
  onClose: () => void;
  workspaceId: string;
}) => {
  const navigate = useNavigate();

  const handleNavigateToBilling = () => {
    onClose();
    navigate(generatePath(routes.workspace.settings.billing, { workspaceId }));
  };

  return (
    <div className="flex flex-col gap-8 justify-between h-full">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>You'll need to upgrade to a paid plan to continue.</div>
      </div>
      <div className="flex flex-row gap-3">
        <Button variant="primary" size="base" onClick={handleNavigateToBilling}>
          See plans
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const PlanManagedByReploContent = ({
  explanationMessage,
  onClose,
  labels,
}: {
  explanationMessage: string;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between h-full">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your plan is managed by Replo. Reach out to{" "}
          <span className="font-bold">support@replo.app</span> to add{" "}
          {labels.plural}. Our team will help you adjust your plan to best suit
          your needs.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <a
          href={`mailto:support@replo.app?subject=Managed Plan - Add ${labels.pluralCapitalized}`}
        >
          <Button variant="primary" size="base">
            Contact us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const IsFromEntitlementContent = ({
  explanationMessage,
  onClose,
  labels,
}: {
  explanationMessage: string;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between h-full">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your plan's maximum number of {labels.plural} has been set manually by
          the Replo team. This could be due to a custom request or a plan
          grandfathered in from our older pricing structure.
          <br />
          <br />
          Reach out to <span className="font-bold">support@replo.app</span> to
          add {labels.plural}. Our team will help you adjust your plan to best
          suit your needs.
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <a
          href={`mailto:support@replo.app?subject=Custom Plan - Add ${labels.pluralCapitalized}`}
        >
          <Button variant="primary" size="base">
            Contact us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ReploSiteEntitlementRejectedModal;
